import { getRegisteredListeners } from '@/app/featureRegistry';
import { setSyncProgress, store } from '@/app/store';
import { setAppOnlineStatus } from '@/app/store/navigationSlice';
import { authManager, mirrorflyLogin } from '@/features/auth/mirrorfly/authUtils';
import { callkitCallbacks } from '@/features/calls';
import { mediaStorage } from '@/features/indexedDB/mediaStorage';
import { profileImageStorage } from '@/features/indexedDB/profileImageStorage';
import { initializeSDK, logout as sdkLogout } from '@/infrastructure/sdk';
import { syncQueueManager } from '@/infrastructure/sync/syncQueueManager';
import { i18n } from '@/localization/i18n';
import * as processENV from '@/processENV';
import { logger, showErrorToast } from '@/shared/utils';
import storage from '@/shared/utils/storage';
import { toast } from 'react-toastify';
import { setupNetworkListeners } from './network';
import { showFullPageLoader } from '@/components/FullPageLoader/fullPageLoaderService';

const { VITE_CALLKIT_URL, VITE_CENTRIFUGO_SOCKET_URL, VITE_LICENSE_KEY, VITE_API_URL } = processENV;
let isLoggingOut = false;

/**
 * Global Connection Listener logic moved to App layer
 * because it depends on feature/auth (authManager).
 */
const connectionListener = res => {
   if (isLoggingOut) return;
   logger.log('App: connectionListener', res);
   const { status } = res || {};
   const toastOptions = { toastId: 'connection-status' };

   if (status === 'CONNECTED') {
      store.dispatch(setAppOnlineStatus({ isConnectionActive: true }));
      toast.dismiss(toastOptions.toastId);
      if (authManager.isDisconnected) {
         authManager.mirrorflyReconnectWithRetries();
      }
   } else if (status === 'DISCONNECTED' || status === 'AUTHFAIL' || status === 'CONNECTIONFAILED') {
      store.dispatch(setAppOnlineStatus({ isConnectionActive: false }));
      if (status === 'DISCONNECTED') showErrorToast(status, { ...toastOptions, autoClose: false });
      authManager.setDisconnected(true);
      if (['AUTHFAIL'].includes(status)) {
         logout();
      }
      if (['CONNECTIONFAILED', 'DISCONNECTED'].includes(status)) {
         authManager.mirrorflyReconnectWithRetries();
      }
   }
};

export const getInitializeObj = (registeredListeners = {}) => ({
   callbackListeners: {
      connectionListener, // App-specific connection logic
      ...registeredListeners, // Feature listeners from featureRegistry (resolved async)
   },
   licenseKey: VITE_LICENSE_KEY,
   centrifugoSocket: VITE_CENTRIFUGO_SOCKET_URL,
   callkitUrl: VITE_CALLKIT_URL,
   apiBaseUrl: VITE_API_URL,
   callKitCallbackListeners: {
      ...callkitCallbacks,
   },
});

export const mirrorflyInitialize = async () => {
   try {
      i18n.init();
      store.dispatch(setSyncProgress(0.1));
      storage.initialize();
      setupNetworkListeners();
      const registeredListeners = await getRegisteredListeners();
      const initObj = getInitializeObj(registeredListeners);
      const mfInit = await initializeSDK(initObj);
      if (!storage.getItem('username')) return;
      if (mfInit?.statusCode === 200) {
         syncQueueManager.startSync();
         await mirrorflyLogin();
         return mfInit;
      }
      logger.error('mirrorflyInitialize failed', mfInit);
   } catch (error) {
      logger.error('mirrorflyInitialize error', error);
      return error;
   }
};

export const initializationPromise = mirrorflyInitialize();

window.resetAppData = async () => {
   logger.log('Resetting application data...');
   try {
      logout();
   } catch (error) {
      logger.error('Error resetting application data:', error);
   }
};

export const logout = async (type = '') => {
   // Guard: if a logout is already in progress, skip to prevent duplicate reloads
   // triggered by concurrent SDK stanza listeners (handleLogout, userDeletedListener)
   // and the direct API handler in useDeleteAccount.
   if (isLoggingOut) return;
   isLoggingOut = true;
   // Preserve theme preference so the login screen respects the user's chosen theme
   const savedTheme = localStorage.getItem('app-theme');
   showFullPageLoader('logout');
   await sdkLogout();
   try {
      await profileImageStorage.clear();
      await mediaStorage.clear();
   } catch (error) {
      logger.error('Error clearing database', error);
   }
   storage.clear();
   localStorage.clear();
   sessionStorage.clear();
   if (savedTheme) {
      localStorage.setItem('app-theme', savedTheme);
   }
   if (type !== 'block') {
      window.location.reload();
   }
};
